import { Add, CloudUpload, Delete, Download } from "@mui/icons-material";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";
import queryDefault from "../../utils/queryDefault";

interface PurchaseItem {
  id?: number;
  qty: number;
  description: string;
  price: number;
}

interface PurchaseForm {
  usage_date: Date | null;
  request_date: Date | null;
  input_link: string;
  items: PurchaseItem[];
  status: string;
  nama_bank: string;
  no_rek: string;
  detail_toko: string;
}

export default function PurchaseEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const notification = useNotification();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadType, setUploadType] = useState<string>("payment proof");

  const form = useForm<PurchaseForm>({
    defaultValues: {
      usage_date: null,
      request_date: null,
      input_link: "",
      items: [{ qty: 1, description: "", price: 0 }],
      status: "",
      nama_bank: "",
      no_rek: "",
      detail_toko: "",
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const { data: purchaseData, isLoading } = useQuery(
    ["purchaseDetail", id],
    () => purchaseService.viewPurchaseDetail(id),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  const { data: filesData, isLoading: filesLoading, refetch: refetchFiles } = useQuery(
    ["purchaseFiles", id],
    () => purchaseService.getPurchaseFiles(Number(id)),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  const files = filesData?.data || [];


  useEffect(() => {
    if (purchaseData?.data) {
      const purchase = purchaseData.data;
      form.reset({
        usage_date: purchase.usage_date ? new Date(purchase.usage_date) : null,
        request_date: purchase.request_date ? new Date(purchase.request_date) : null,
        input_link: purchase.input_link || "",
        items: purchase.items || [{ qty: 1, description: "", price: 0 }],
        status: purchase.status || "",
        nama_bank: purchase.nama_bank || "",
        no_rek: purchase.no_rek || "",
        detail_toko: purchase.detail_toko || "",
      });
    }
  }, [purchaseData, form]);

  const updatePurchase = useMutation(
    (params: any) => purchaseService.updatePurchase({ ...params, id }),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Purchase updated successfully!",
          type: "success",
          position: "top",
        });
        navigate("/app/purchase");
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to update purchase",
          type: "error",
          position: "top",
        });
        setIsSubmitting(false);
      },
    }
  );

  const uploadFile = useMutation(
    ({ file, type }: { file: File; type: string }) => purchaseService.uploadPurchaseFile(Number(id), file, type),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "File uploaded successfully!",
          type: "success",
          position: "top",
        });
        refetchFiles();
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to upload file",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteFile = useMutation(
    (fileId: number) => purchaseService.deletePurchaseFile(fileId),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "File deleted successfully!",
          type: "success",
          position: "top",
        });
        refetchFiles();
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to delete file",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const onSubmit = (data: PurchaseForm) => {
    setIsSubmitting(true);
    const payload = {
      usage_date: data.usage_date?.toISOString().split('T')[0],
      request_date: data.request_date?.toISOString().split('T')[0],
      input_link: data.input_link,
      items: data.items,
      last_updated_by: user.id,
      status: data.status,
      nama_bank: data.nama_bank,
      no_rek: data.no_rek,
      detail_toko: data.detail_toko,
    };
    updatePurchase.mutate(payload);
  };

  const addItem = () => {
    append({ qty: 1, description: "", price: 0 });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && id) {
      uploadFile.mutate({ file, type: uploadType });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <MainLayout>
      <Helmet>
        <title>Edit Purchase</title>
      </Helmet>

      <Breadcrumb
        label="Edit Purchase"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Purchase Management", href: "/app/purchase" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Purchase Details
          </Typography>
          <Box component="form" onSubmit={form.handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <Controller
                name="usage_date"
                control={form.control}
                render={({ field }) => (
                  <DatePicker
                    label="Usage Date"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                )}
              />

              <Controller
                name="request_date"
                control={form.control}
                render={({ field }) => (
                  <DatePicker
                    label="Request Date"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                )}
              />

              <Controller
                name="input_link"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Input Link"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Controller
                  name="nama_bank"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} label="Bank Name" fullWidth variant="outlined" />
                  )}
                />

                <Controller
                  name="no_rek"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} label="Account Number" fullWidth variant="outlined" />
                  )}
                />
              </Stack>

              <Controller
                name="detail_toko"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Store Detail"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                )}
              />

              <Controller
                name="status"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <Box>
                    <Select
                      {...field}
                      size="small"
                      error={Boolean(fieldState.error?.message)}
                      fullWidth
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="processed">Processed</MenuItem>
                      <MenuItem value="declined">Declined</MenuItem>
                      <MenuItem value="done">Done</MenuItem>
                    </Select>

                    <FormHelperText
                      error={Boolean(fieldState.error?.message)}
                    >
                      {fieldState.error?.message}
                    </FormHelperText>
                  </Box>
                )}
              />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Purchase Items
                </Typography>
                <Stack spacing={2}>
                  {fields.map((field, index) => (
                    <Stack key={field.id} direction="row" spacing={2} alignItems="center">
                      <Controller
                        name={`items.${index}.qty`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Qty"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.description`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Description"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.price`}
                        control={form.control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Price"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <IconButton
                        onClick={() => removeItem(index)}
                        disabled={fields.length === 1}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addItem}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add Item
                  </Button>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Purchase"}
                </Button>
                {(user.role_id === 1 || user.role_id === 2) && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => { setUploadType("payment proof"); fileInputRef.current?.click(); }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload Payment Proof"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => { setUploadType("npwp"); fileInputRef.current?.click(); }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload NPWP"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => { setUploadType("ktp"); fileInputRef.current?.click(); }}
                      disabled={uploadFile.isLoading}
                    >
                      {uploadFile.isLoading ? "Uploading..." : "Upload KTP"}
                    </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  onClick={() => navigate("/app/purchase")}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
          />

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Attached Files
          </Typography>
          {filesLoading ? (
            <Typography>Loading files...</Typography>
          ) : files.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{file.file_path.split('/').pop()}</TableCell>
                      <TableCell>{file.type || "N/A"}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton onClick={() => purchaseService.downloadPurchaseFile(file.id)}>
                            <Download />
                          </IconButton>
                          {(user.role_id === 1 || file.created_by === user.id) && (
                            <IconButton color="error" onClick={() => deleteFile.mutate(file.id)}>
                              <Delete />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No files attached</Typography>
          )}
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}